import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { Main, Styled } from "./style";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { AuthenticateSelect } from "../../../../auth/select";
import { Switch } from "../../../../form/switch";

type WhatsappSendMessageProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function WhatsappSendMessage({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: WhatsappSendMessageProps) {
  const { setNodes, getNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setIsExpanded((p) => !p));
    if (setChildSettings)
      setChildSettings(() =>
        setSettings((p) => ({ ...p, isToggle: !p.isToggle })),
      );
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (data.value?.isVoiceNote === undefined) {
      updateNodeValue(id, setNodes, { isVoiceNote: false });
    }
  }, []);

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    keyRef?: string,
  ) => keyRef && updateNodeValue(id, setNodes, { [keyRef]: e.target.value });

  const useConfirmationMessages = Boolean(
    data.value?.useConfirmationMessages || data.value?.useButtons,
  );

  const handleToggleConfirmationMessages = () => {
    const nextUseConfirmationMessages = !useConfirmationMessages;

    if (nextUseConfirmationMessages) {
      updateNodeValue(id, setNodes, {
        useConfirmationMessages: true,
        useButtons: false,
        confirmationMessage:
          data.value?.confirmationMessage || "Reply Confirm to confirm.",
        cancellationMessage:
          data.value?.cancellationMessage || "Reply Cancel to cancel.",
        mediaUrl: "",
        mediaType: undefined,
        mimeType: "",
        fileName: "",
        caption: "",
      });
      return;
    }

    updateNodeValue(id, setNodes, {
      useConfirmationMessages: false,
      useButtons: false,
    });
  };

  const handleToggleVoiceNote = () => {
    updateNodeValue(id, setNodes, {
      isVoiceNote: !(data.value?.isVoiceNote !== false),
    });
  };

  // Check if there's a WhatsApp listener in the workflow
  const hasWhatsAppListener = () => {
    const nodes = getNodes();
    return nodes.some((node: any) => node.data.action === "whatsappListener");
  };

  const listenerExists = hasWhatsAppListener();
  const isWarning = !data.value?.connection || !listenerExists;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded && (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            style={{ minWidth: "420px" }}
            isWarning={isWarning}>
            <Styled>
              {!listenerExists && (
                <div className="warning-box">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-text">
                    <strong>High-Volume Messaging Risk</strong>
                    <p>
                      Sending too many WhatsApp messages in a short time can
                      flag your number and may cause a temporary or permanent
                      block. Add a "WhatsApp Listener" trigger and use this
                      node for controlled replies/follow-ups instead of bulk
                      sending.
                    </p>
                  </div>
                </div>
              )}

              <>
                <AuthenticateSelect
                  type="whatsapp"
                  defaultId={
                    data.value?.connection?._id || data.value?.connectionId
                  }
                  handleAuthenticate={(type, connection) => {
                    if (type === "add") {
                      const connectionId = connection._id || connection.id;
                      updateNodeValue(id, setNodes, {
                        connection,
                        connectionId,
                      });
                    } else {
                      updateNodeValue(id, setNodes, {
                        connection: null,
                        connectionId: null,
                      });
                    }
                  }}
                  withLine={true}
                  background={data.background}
                />

                <div style={{ marginBottom: "10px" }}>
                  <TextInput
                    keyRef="phoneNumber"
                    value={data.value?.phoneNumber || ""}
                    placeholder="Enter phone number (e.g., 201005307391)"
                    handleChange={handleChangeInput}
                  />
                </div>

                <Textarea
                  keyRef="message"
                  value={data.value?.message || ""}
                  placeholder={
                    useConfirmationMessages
                      ? "Enter the main confirmation question/message"
                      : "Enter your message or leave empty when sending voice/media"
                  }
                  handleChange={handleChangeInput}
                />

                {/* <div className="buttons-config">
                  <Switch
                    title="Use confirmation messages"
                    value={useConfirmationMessages}
                    setValue={handleToggleConfirmationMessages}
                  />

                  {useConfirmationMessages && (
                    <div className="buttons-editor">
                      <p className="buttons-helper">
                        Sends normal WhatsApp text with confirmation and
                        cancellation instructions. No buttons or polls.
                      </p>

                      <Textarea
                        title="Confirmation Message"
                        keyRef="confirmationMessage"
                        value={
                          data.value?.confirmationMessage ||
                          "Reply Confirm to confirm."
                        }
                        placeholder="Reply Confirm to confirm."
                        handleChange={handleChangeInput}
                      />

                      <Textarea
                        title="Cancellation Message"
                        keyRef="cancellationMessage"
                        value={
                          data.value?.cancellationMessage ||
                          "Reply Cancel to cancel."
                        }
                        placeholder="Reply Cancel to cancel."
                        handleChange={handleChangeInput}
                      />
                    </div>
                  )}
                </div> */}

                {!useConfirmationMessages && data.value?.isVoiceNote && (
                  <>
                    <div style={{ marginBottom: "10px" }}>
                      <TextInput
                        title="Voice URL"
                        keyRef="mediaUrl"
                        value={data.value?.mediaUrl || ""}
                        placeholder="https://.../speech.wav or {{ $block.textToSpeech-1.output }}"
                        handleChange={handleChangeInput}
                      />
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <TextInput
                        title="Type"
                        keyRef="mimeType"
                        value={data.value?.mimeType || ""}
                        placeholder="audio/wav (Optional)"
                        handleChange={handleChangeInput}
                      />
                    </div>

                  </>
                )}
                    <div className="voice-note-config">
                    </div>
                <Switch
                  title="Send as voice note"
                  value={data.value?.isVoiceNote !== false}
                  setValue={handleToggleVoiceNote}
                />
              </>
            </Styled>
          </ExpandedBlock>
        )}
      </Main>
    </>
  );
}
